import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { GAME_KIND_LABELS, GAME_KINDS, type Game, type GameKind } from '@goodbuild/shared';
import { GameKindBanner, PageHead, ShelfHead } from '../ui/index.js';
import { useAccess, useGames } from '../api/hooks.js';
import { useInstalledGames } from '../components/game/useInstallState.js';
import { FocusZone } from '../components/focus/Focusable.js';
import { MenuButton, type MenuOption } from '../components/focus/Menu.js';
import { useGamepadButton } from '../components/focus/useGamepad.js';
import { GameCard } from '../components/game/GameCard.js';
import { ShelfBar } from '../components/game/ShelfBar.js';
import { SupporterCta } from '../components/layout/SupporterCta.js';
import { EmptyState, Loading } from '../components/layout/Loading.js';
import { compareTitles, matchesQuery, newestBuildAt, normalise } from '../library/view.js';

/** All, then the ladder in order. Nothing here is about this machine. */
const SHOWS = ['all', ...GAME_KINDS] as const;
type Show = (typeof SHOWS)[number];

const SHOW_LABELS: Record<Show, string> = { all: 'All', ...GAME_KIND_LABELS };

function isKind(show: Show): show is GameKind {
  return (GAME_KINDS as readonly string[]).includes(show);
}

function isShow(value: unknown): value is Show {
  return typeof value === 'string' && (SHOWS as readonly string[]).includes(value);
}

/**
 * How the catalogue is arranged.
 *
 * Two, where the library has four. The other two the library offers — what you last played and
 * what it takes up on disk — are facts about this machine, and this screen is not about this
 * machine.
 */
const SORTS = ['newest', 'alpha'] as const;
type Sort = (typeof SORTS)[number];

const SORT_LABELS: Record<Sort, string> = { newest: 'Newest build', alpha: 'Alphabetical' };

function isSort(value: unknown): value is Sort {
  return typeof value === 'string' && (SORTS as readonly string[]).includes(value);
}

export const Route = createFileRoute('/games/')({
  component: Games,
  /** Both live in the address, so a shelf narrowed to the prototypes is a link somebody can send. */
  validateSearch: (search: Record<string, unknown>): { show?: Show; sort?: Sort } => ({
    ...(isShow(search.show) ? { show: search.show } : {}),
    ...(isSort(search.sort) ? { sort: search.sort } : {}),
  }),
});

/** Newest build first by default: a catalogue is opened to find out what is new in it. */
function arrange(games: Game[], sort: Sort): Game[] {
  const sorted = [...games];
  return sort === 'alpha'
    ? sorted.sort(compareTitles)
    : sorted.sort((a, b) => newestBuildAt(b) - newestBuildAt(a) || compareTitles(a, b));
}

/**
 * Games: everything we make, laid out to be chosen from (`03-client-app.md` section 4.2).
 *
 * This is the shop window, and it is deliberately not the library. A library is a room of games
 * somebody has already decided about, so a tile there does the thing — press it and it plays,
 * or starts arriving. Here nothing is decided yet, so a tile does what a tile in a shop does:
 * it opens the game's page, where the trailers, the screenshots, the build facts and the way in
 * all are. There is no install on this screen, no play, no pause and no uninstall. Those belong
 * to the games somebody already has, which is what Library is for.
 *
 * What is left is what browsing actually needs: a search, a sort, the four rungs of the ladder
 * to filter by, and the artwork at a size worth looking at. Filtering to one rung puts that
 * rung's warning banner over the grid, so nobody has to open a game to find out what "Dev" was
 * promising them.
 *
 * Open to everyone, supporter or not, because this shelf is how somebody decides the membership
 * is worth it — and it is the same screen in a browser as in the app, because nothing on it
 * ever needed a machine that can install anything. A game already on this machine is marked as
 * such and no more than that: a note that you have it, not a button that runs it.
 */
function Games() {
  const navigate = useNavigate();
  const { show = 'all', sort = 'newest' } = Route.useSearch();
  const { hasAccess } = useAccess();
  const { data, isLoading } = useGames();
  const { games: installed } = useInstalledGames();

  /** Held here rather than in the address: this filters what is already on the screen. */
  const [query, setQuery] = React.useState('');

  // Both written out rather than spread over whatever is in the address already: this screen
  // and the library both call a search parameter `sort` and mean different sets of values by
  // it, so carrying one over from the other would put "install size" on a catalogue.
  const setShow = React.useCallback(
    (next: Show) => void navigate({ to: '/games', search: { show: next, sort }, replace: true }),
    [navigate, sort],
  );

  const setSort = React.useCallback(
    (next: Sort) => void navigate({ to: '/games', search: { show, sort: next }, replace: true }),
    [navigate, show],
  );

  // LB and RB walk the rungs without opening the picker, which is what the shoulder buttons do
  // for every strip of tabs in this app (section 3.3).
  const move = (delta: number) => {
    const index = SHOWS.indexOf(show);
    setShow(SHOWS[(index + delta + SHOWS.length) % SHOWS.length]!);
  };
  useGamepadButton('lb', () => move(-1));
  useGamepadButton('rb', () => move(1));

  const all = React.useMemo(() => data ?? [], [data]);
  const installedIds = React.useMemo(
    () => new Set(installed.map((game) => game.gameId)),
    [installed],
  );

  if (isLoading) return <Loading label="Opening the catalogue" />;

  const needle = normalise(query);
  const shown = arrange(
    all.filter((game) => matchesQuery(game, needle) && (show === 'all' || game.kind === show)),
    sort,
  );

  return (
    <FocusZone focusKey="games" direction="column" className="gb-stack gb-stack--loose">
      <PageHead
        eyebrow="Everything we make"
        title="Games."
        description={
          hasAccess
            ? 'Every game and every prototype, with the build we pushed most recently. Open one for the shots, the notes and the way in.'
            : `Every one of these is included in a membership.${
                all.length > 0 ? ` ${all.length} and counting.` : ''
              }`
        }
      />

      <ShelfBar query={query} onQuery={setQuery} focusKey="games-bar" label="Search the catalogue">
        <MenuButton
          label="Sort"
          value={SORT_LABELS[sort]}
          focusKey="games-sort"
          options={SORTS.map<MenuOption<Sort>>((key) => ({
            id: key,
            label: SORT_LABELS[key],
            checked: key === sort,
          }))}
          onPick={setSort}
        />

        <MenuButton
          label="Show"
          value={SHOW_LABELS[show]}
          focusKey="games-show"
          align="end"
          options={SHOWS.map<MenuOption<Show>>((key) => ({
            id: key,
            label: SHOW_LABELS[key],
            checked: key === show,
          }))}
          onPick={setShow}
        />
      </ShelfBar>

      {/* Compact, because nothing on this screen is the decision itself — the full-height
          version is on the game's own page, over the install button. */}
      {isKind(show) ? <GameKindBanner kind={show} compact /> : null}

      {shown.length === 0 ? (
        <EmptyState
          title={needle.length > 0 ? `Nothing matches “${query}”.` : 'No games here yet.'}
          body={
            needle.length > 0
              ? 'Try a shorter word, or clear the search to see the whole catalogue.'
              : 'The first build lands here the day we push it.'
          }
        />
      ) : (
        <section className="gb-section">
          <ShelfHead
            title={SHOW_LABELS[show]}
            note={`${shown.length} ${shown.length === 1 ? 'game' : 'games'}`}
          />
          <FocusZone focusKey="games-grid" className="gb-grid gb-grid--capsule">
            {shown.map((game, index) => (
              <GameCard
                key={game.id}
                game={game}
                caption="name"
                installed={installedIds.has(game.id)}
                autoFocus={index === 0}
              />
            ))}
          </FocusZone>
        </section>
      )}

      {hasAccess ? null : <SupporterCta gameCount={all.length} />}
    </FocusZone>
  );
}
